import {
  Body1Strong,
  Dropdown,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Option,
  SelectionEvents,
  OptionOnSelectData,
  makeStyles,
  tokens,
  Subtitle2,
} from '@fluentui/react-components';
import { Info24Regular } from '@fluentui/react-icons';
import { useContext } from 'react';

import { AccessType, TAccessInfo, TAccessNodeType } from '../../types';
import { useUpdateAccess } from '../../api';
import { USER_GROUPS, TUser, SignInContext, Avatar } from '@modules/users';

import { ControlledTooltip } from '@shared/components';

import { accessLabel } from '@shared/backendFrontend';

import AddUserForm from './AddUserForm';

type TProps = {
  id: string;
  nodeType: TAccessNodeType;
  access: TAccessInfo;
  users: TUser[];
  setIsLoading: (isLoading: boolean) => void;
};

type TAccessUser = {
  user: TUser;
  accessType: AccessType;
  inheritedAccessRight: AccessType;
  inheritedAll: boolean;
};

const useStyles = makeStyles({
  dropdown: {
    minWidth: 0,
    width: '97%',
  },
  info: {
    color: tokens.colorBrandBackground,
  },
});

const AccessContent = ({ id, nodeType, access, users, setIsLoading }: TProps) => {
  // --- ACCESS RIGHTS:
  const usersNoAccess: TUser[] = [];
  const usersInheritedAccess: TAccessUser[] = [];
  const usersDirectAccess: TAccessUser[] = [];

  users.forEach((user) => {
    if (!user.inviteAccepted) return;

    // admin:
    const isAdmin = user.groups.includes(USER_GROUPS.ADMIN);
    if (isAdmin)
      return usersInheritedAccess.push({
        user,
        accessType: AccessType.MANAGE,
        inheritedAccessRight: AccessType.MANAGE,
        inheritedAll: true,
      });

    const userAccessEntry = access.users.find((userAccessEntry) => userAccessEntry.user === user._id);
    const userAccessEntryTotal = access.totalUsers.find((userAccessEntry) => userAccessEntry.user === user._id);
    const userAccessEntryTotalParent = access.totalUsersParent.find(
      (userAccessEntry) => userAccessEntry.user === user._id
    );

    // no-access:
    if (!userAccessEntryTotal || userAccessEntryTotal.accessType === AccessType.PARTIAL)
      return usersNoAccess.push(user);

    const inheritedAccessRight = userAccessEntryTotalParent ? userAccessEntryTotalParent.accessType : AccessType.NONE;

    // inherited more or equal access than direct:
    const isInherited = !userAccessEntry || userAccessEntry.accessType <= inheritedAccessRight;
    if (isInherited)
      usersInheritedAccess.push({
        user,
        accessType: userAccessEntryTotal.accessType,
        inheritedAccessRight,
        inheritedAll: true,
      });
    else
      usersDirectAccess.push({
        user,
        accessType: userAccessEntryTotal.accessType,
        inheritedAccessRight,
        inheritedAll: false,
      });
  });
  const usersAccess = [...usersDirectAccess, ...usersInheritedAccess];

  const updateAccess = useUpdateAccess();
  const { user } = useContext(SignInContext);
  const getOnOptionSelect = (accessUser: TAccessUser) => (event: SelectionEvents, data: OptionOnSelectData) => {
    if (data.selectedOptions.length !== 1) return;
    const newAccessType = parseInt(data.selectedOptions[0]) as AccessType;
    if (newAccessType === accessUser.accessType) return;

    setIsLoading(true);
    updateAccess.mutate(
      {
        id,
        nodeType,
        userId: accessUser.user._id,
        accessType: newAccessType,
      },
      {
        onSettled: () => setIsLoading(false),
        onSuccess: () => {
          if (accessUser.user._id === user._id && newAccessType < AccessType.MANAGE) window.location.reload();
        },
      }
    );
  };

  const accessTypeOptions: AccessType[] = [AccessType.NONE, AccessType.READ, AccessType.EDIT, AccessType.MANAGE];

  const classes = useStyles();
  return (
    <div>
      <p className="pb-3">
        <Subtitle2>Add user</Subtitle2>
      </p>
      <AddUserForm id={id} nodeType={nodeType} users={usersNoAccess} setIsLoading={setIsLoading} />
      <p className="pb-3 mt-8">
        <Subtitle2>Users with access</Subtitle2>
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              <Body1Strong>User</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell>
              <Body1Strong>Access</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '70px' }}>
              <Body1Strong>Info</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {usersAccess.map((userAccess) => (
            <TableRow key={userAccess.user._id}>
              <TableCell>
                <TableCellLayout media={<Avatar user={userAccess.user} tooltip="mail" />}>
                  {userAccess.user.name}
                </TableCellLayout>
              </TableCell>
              <TableCell>
                <Dropdown
                  appearance="underline"
                  selectedOptions={[userAccess.accessType.toString()]}
                  value={accessLabel(userAccess.accessType)}
                  onOptionSelect={getOnOptionSelect(userAccess)}
                  className={classes.dropdown}
                >
                  {accessTypeOptions.map((accessType) => (
                    <ControlledTooltip
                      key={accessType}
                      enable={
                        userAccess.inheritedAccessRight > accessType &&
                        userAccess.inheritedAccessRight > AccessType.PARTIAL
                      }
                      content={
                        userAccess.user.groups.includes(USER_GROUPS.ADMIN)
                          ? 'User is global admin.'
                          : 'User inherits this access right from a parent item.'
                      }
                      relationship="inaccessible"
                    >
                      <Option
                        value={accessType.toString()}
                        disabled={
                          userAccess.inheritedAccessRight > accessType &&
                          userAccess.inheritedAccessRight > AccessType.PARTIAL
                        }
                      >
                        {accessLabel(accessType)}
                      </Option>
                    </ControlledTooltip>
                  ))}
                </Dropdown>
              </TableCell>
              <TableCell>
                {userAccess.inheritedAll && (
                  <ControlledTooltip
                    content={
                      userAccess.user.groups.includes(USER_GROUPS.ADMIN)
                        ? 'User is global admin.'
                        : 'User inherits access rights from a parent item.'
                    }
                    relationship="description"
                  >
                    <Info24Regular className={classes.info} />
                  </ControlledTooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default AccessContent;
